import React, { useState, useEffect, useRef } from "react";
import { IoCloseOutline, IoDownloadOutline } from "react-icons/io5";
import { motion } from "framer-motion";

const ContentModals = ({ setModal, data }) => {
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const canvasRefs = useRef([]);
  const containerRef = useRef(null);

  // Load pdf.js
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.min.js";
    script.async = true;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";
      console.log("pdf.js loaded successfully");
    };
    script.onerror = () => {
      console.error("Failed to load pdf.js");
      setError("Failed to load PDF renderer. Please try again.");
    };
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  // Fetch and load PDF
  useEffect(() => {
    let blobUrl;
    setLoading(true);
    setError(null);
    setDownloadUrl(null);
    if (data?.contentFile && window.pdfjsLib) {
      fetch(data.contentFile)
        .then((response) => {
          if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.statusText}`);
          return response.blob();
        })
        .then((blob) => {
          blobUrl = URL.createObjectURL(blob);
          setDownloadUrl(blobUrl);
          window.pdfjsLib.getDocument(blobUrl).promise.then((pdfDoc) => {
            setPdf(pdfDoc);
            setNumPages(pdfDoc.numPages);
            setLoading(false);
            console.log(`PDF loaded with ${pdfDoc.numPages} pages`);
          }).catch((err) => {
            console.error("Error loading PDF document:", err);
            setError("Failed to load the document. Please try again.");
            setLoading(false);
          });
        })
        .catch((error) => {
          console.error("Error fetching PDF:", error);
          setError("Failed to load the document. Please try again or contact support.");
          setLoading(false);
        });
      return () => {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      };
    } else {
      setError("No document available or PDF renderer not loaded.");
      setLoading(false);
      console.error("No PDF URL or pdf.js not loaded");
    }
  }, [data?.contentFile]);

  // Update screen size
  useEffect(() => {
    const updateScreenSize = () => {
      const isLarge = window.innerWidth >= 1024;
      setIsLargeScreen(isLarge);
      console.log(`Screen size: ${window.innerWidth}px, isLargeScreen: ${isLarge}`);
    };
    window.addEventListener("resize", updateScreenSize);
    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  // Render PDF pages with lazy-loading
  useEffect(() => {
    if (pdf && canvasRefs.current.length > 0 && containerRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const pageNum = parseInt(entry.target.dataset.page);
              const renderPage = async () => {
                try {
                  const page = await pdf.getPage(pageNum);
                  const scale = isLargeScreen ? 1.5 : 1.0;
                  const viewport = page.getViewport({ scale });
                  const canvas = canvasRefs.current[pageNum - 1];
                  if (canvas) {
                    const context = canvas.getContext("2d");
                    // Adjust canvas size to fit container width
                    const containerWidth = containerRef.current.offsetWidth;
                    const adjustedScale = Math.min(scale, containerWidth / viewport.width);
                    const adjustedViewport = page.getViewport({ scale: adjustedScale });
                    canvas.height = adjustedViewport.height;
                    canvas.width = adjustedViewport.width;
                    const renderContext = {
                      canvasContext: context,
                      viewport: adjustedViewport,
                    };
                    await page.render(renderContext).promise;
                    console.log(`Rendered page ${pageNum} at scale ${adjustedScale}`);
                  }
                } catch (error) {
                  console.error(`Error rendering page ${pageNum}:`, error);
                }
              };
              renderPage();
            }
          });
        },
        { root: containerRef.current, threshold: 0.1 }
      );

      canvasRefs.current.forEach((canvas) => {
        if (canvas) observer.observe(canvas);
      });

      return () => observer.disconnect();
    }
  }, [pdf, numPages, isLargeScreen]);

  // Handle download
  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = (data?.contentSectionName || "document") + ".pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="fixed top-10 w-[90%] lg:absolute lg:top-14">
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: [0.1, 0.3, 0.5, 0.7, 1] }}
        transition={{ duration: 0.7, ease: "easeIn" }}
        exit={{ opacity: 0, transition: { duration: 1.5, ease: "easeIn" } }}
        className="w-full max-w-4xl h-[95vh] max-h-[95vh] bg-white pt-5 shadow-md rounded-xl md:pt-6 mx-auto"
      >
        <div className="flex items-start justify-between w-full px-3 md:px-5 md:items-center lg:px-7">
          <p className="text-[11px] text-[#101828] font-semibold w-[75%] md:text-[12px] lg:text-sm xl:text-base">
            {data?.contentSectionName || "Document"}
          </p>
          <div className="flex items-center gap-3">
            {downloadUrl && (
              <button
                onClick={handleDownload}
                className="text-[#25A249] hover:text-[#316941] flex items-center gap-1 text-sm"
              >
                <IoDownloadOutline className="text-xl lg:text-2xl" />
                Download
              </button>
            )}
            <IoCloseOutline
              onClick={() => setModal(false)}
              className="text-xl lg:text-2xl xl:text-3xl cursor-pointer"
            />
          </div>
        </div>
        <div className="w-full mt-2.5 bg-[#DDE1E6] border-[1px] border-dashed lg:mt-3.5"></div>
        <div
          ref={containerRef}
          className="w-full h-[calc(95vh-80px)] overflow-y-auto flex flex-col items-center"
        >
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-600">
              Loading document...
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full text-red-600">
              {error}
            </div>
          ) : (
            <div className='flex flex-col items-center w-full px-2 h-[750px] lg:h-[725px] xl:h-full'>
              {Array.from({ length: numPages }, (_, index) => (
                <canvas
                  key={index}
                  ref={(el) => (canvasRefs.current[index] = el)}
                  data-page={index + 1}
                  className="w-auto max-w-[95%] mb-4 block"
                  style={{ display: "block" }}
                />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ContentModals
